/**
 * useYjsExtension React Hook
 *
 * Manages Yjs CodeMirror extension lifecycle in React components.
 * Handles Y.UndoManager creation/destruction and provides memoized extension.
 *
 * @module features/collab/yjs/hooks/useYjsExtension
 * @see SPEC-008 Requirements 1.6, 1.8
 */

import { useMemo, useEffect } from 'react';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { createYjsExtension, type YjsExtensionResult } from '../createYjsExtension';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for useYjsExtension hook
 */
export interface UseYjsExtensionOptions {
  /**
   * Capture timeout for UndoManager in milliseconds.
   * Groups edits within this timeframe into a single undo step.
   * @default 500
   */
  captureTimeout?: number;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React hook for managing Yjs CodeMirror extension lifecycle.
 *
 * Creates and manages Y.UndoManager with proper trackedOrigins configuration:
 * - Only tracks local edits (origin: null)
 * - Does not track remote collaboration updates
 *
 * @param yDoc - Y.Doc instance from YjsSyncManager, or null if not ready
 * @param awareness - Awareness instance for presence, or null/undefined if not available
 * @param options - Optional configuration
 * @returns Extension result or null if yDoc is not provided
 *
 * @example
 * ```typescript
 * function CollaborativeEditor({ docId }) {
 *   const { connection } = useRoomConnection(docId, userId, displayName, { autoConnect: true });
 *   const syncManager = useSyncManager(docId);
 *
 *   // Get Yjs extension - awareness from connection if available
 *   const yjsExtension = useYjsExtension(
 *     syncManager?.getDoc() ?? null,
 *     connection?.awareness ?? null
 *   );
 *
 *   // Use extension in EditorState.create()
 *   useEffect(() => {
 *     if (!yjsExtension) return;
 *
 *     const state = EditorState.create({
 *       doc: yjsExtension.yText.toString(),
 *       extensions: [basicSetup, yjsExtension.extension],
 *     });
 *
 *     const view = new EditorView({ state, parent: containerRef.current });
 *
 *     return () => view.destroy();
 *   }, [yjsExtension]);
 *
 *   return <div ref={containerRef} />;
 * }
 * ```
 */
export function useYjsExtension(
  yDoc: Y.Doc | null,
  awareness: Awareness | null | undefined,
  options: UseYjsExtensionOptions = {}
): YjsExtensionResult | null {
  const { captureTimeout = 500 } = options;

  // Create extension with memoization
  // Dependencies: yDoc and awareness (shallow comparison)
  const result = useMemo(() => {
    if (!yDoc) return null;

    const yText = yDoc.getText('content');

    // Create UndoManager with proper configuration:
    // - captureTimeout: Groups rapid edits into single undo step
    // - trackedOrigins: Set([null]) means only track local edits
    //   - null = edits without explicit origin (CodeMirror default)
    //   - REMOTE_ORIGIN = remote updates (excluded from undo)
    const undoManager = new Y.UndoManager(yText, {
      captureTimeout,
      trackedOrigins: new Set([null]),
    });

    return createYjsExtension({
      yDoc,
      awareness: awareness ?? undefined,
      undoManager,
    });
  }, [yDoc, awareness, captureTimeout]);

  // Cleanup on unmount or when result changes
  useEffect(() => {
    return () => {
      result?.destroy();
    };
  }, [result]);

  return result;
}
